/**
 * Globaler Artikel-Filter in der Filter-Sidebar.
 *
 * Ersetzt das frühere `artikel_bezeichnung`-Accordion (61k Werte, nur nach
 * Name filterbar). Sucht über /artikel/search nach Bezeichnung, EAN/PLU,
 * Mono-Nr (artikel_nr) und DRS-Nr (artikel_id) und setzt einen globalen
 * `ean`-Filter — auf den seit dem Positionen-Routing ALLE Charts und KPIs
 * reagieren. Labels der gewählten Artikel wandern mit in die URL (teilbar).
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Icon } from './Icon.tsx';

const EUR = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export function artikelLabel(hit: {
  name: string | null;
  artikel_nr: string | null;
  artikel_id: string | null;
  ean: string;
}): string {
  const nr = hit.artikel_nr || hit.artikel_id || hit.ean;
  return hit.name ? `${nr} — ${hit.name}` : String(nr);
}

export function ArtikelFilterPanel() {
  const { filters, labels, addFilterWithLabel, removeFilter } = useFilterStore();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ['artikel-search', debounced],
    queryFn: () => api.artikelSearch(debounced),
    enabled: debounced.length >= 2,
  });

  const selected = Array.isArray(filters.ean) ? filters.ean : [];
  const results = (data?.results ?? []).slice(0, 12);

  return (
    <details open={selected.length > 0 || q.length > 0} className="group px-2">
      <summary className="flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 list-none">
        <span className="flex items-center gap-1.5 font-mono text-2xs uppercase tracking-[0.12em] font-medium text-slate-500 dark:text-slate-400">
          <Icon
            name="chevronRight"
            size={11}
            className="group-open:rotate-90 transition-transform"
          />
          Artikel
        </span>
        {selected.length > 0 && <span className="badge-active">{selected.length}</span>}
      </summary>

      <div className="mt-1 pl-3 space-y-1.5">
        {/* Aktive Artikel-Filter */}
        {selected.map((ean) => (
          <button
            key={ean}
            onClick={() => removeFilter('ean', ean)}
            className="w-full flex items-center justify-between text-left text-xs px-2 py-1 rounded-md bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 font-medium hover:bg-brand-100 dark:hover:bg-brand-900 transition-colors cursor-pointer"
            title="Filter entfernen"
          >
            <span className="truncate flex-1 flex items-center gap-1.5">
              <Icon name="check" size={11} />
              {labels[`ean:${ean}`] ?? ean}
            </span>
            <Icon name="x" size={11} className="shrink-0 ml-1 opacity-60" />
          </button>
        ))}

        {/* Suche */}
        <div className="relative">
          <Icon
            name="search"
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nr, EAN, PLU oder Name …"
            className="w-full pl-7 pr-2 py-1 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
          />
        </div>

        {/* Ergebnisse */}
        {debounced.length >= 2 && (
          <ul className="space-y-px">
            {isFetching && (
              <li className="px-2 py-1 text-2xs text-slate-400">Suche …</li>
            )}
            {!isFetching && results.length === 0 && (
              <li className="px-2 py-1 text-2xs text-slate-400">Keine Treffer.</li>
            )}
            {results
              .filter((r) => !selected.includes(r.ean))
              .map((r) => (
                <li key={r.ean}>
                  <button
                    onClick={() => {
                      addFilterWithLabel('ean', r.ean, artikelLabel(r));
                      setQ('');
                    }}
                    className="w-full text-left text-xs px-2 py-1 rounded-md text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    title={`${r.name ?? r.ean}\nEAN ${r.ean}${r.artikel_nr ? ` · Mono-Nr ${r.artikel_nr}` : ''}${r.artikel_id ? ` · DRS-Nr ${r.artikel_id}` : ''}`}
                  >
                    <span className="block truncate">{r.name ?? r.ean}</span>
                    <span className="block text-2xs text-slate-400 tabular-nums truncate">
                      {[r.artikel_nr && `Nr ${r.artikel_nr}`, r.artikel_id && `ID ${r.artikel_id}`, r.ean]
                        .filter(Boolean)
                        .join(' · ')}
                      {r.umsatz > 0 && (
                        <span className="float-right">{EUR.format(r.umsatz)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </details>
  );
}
